import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Ban, Blocks, CheckCircle2, Crown, Gauge, Handshake, LogIn, Save, Workflow, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, errorMessage, unwrap, type S } from "../../api/client";
import { useLuxWallet } from "../../api/queries";
import { LuxInput } from "../../components/LuxInput";
import { LuxGlyph } from "../../components/LuxIcon";
import { Activity } from "../Lux";
import { Alert, Badge, Button, Card, Field, Modal, NumberInput, PageHead, Select, Spinner, Switch, TextInput, useToast } from "../../components/ui";
import { StatusBadge } from "../../components/StatusBadge";
import { initials } from "../../lib/format";
import { useT } from "../../i18n/I18nProvider";
import { DirIcon } from "../../i18n/mirror";
import { useFmt } from "./format";
import { OrgPipeline } from "./OrgPipeline";
import { OrgPlan } from "./OrgPlan";
import { OrgBilling } from "./OrgBilling";
import { OrgIntegrations } from "./Integrations";
import { useOpenCompany } from "./Orgs";
import { ROLE_TONE } from "../../api/org";

function GrantForm({ orgId, onDone }: { orgId: string; onDone: () => void }) {
  const toast = useToast();
  const t = useT();
  const { lux } = useFmt();
  const [kind, setKind] = useState<"grant" | "refund">("grant");
  const [amount, setAmount] = useState<number | undefined>(1000);
  const [ref, setRef] = useState("");
  const [note, setNote] = useState("");
  // Every grant carries its own expiry (required by the API): a number of months, or the last day it may be spent
  const [expiry, setExpiry] = useState<string>("12");
  const [expiryOn, setExpiryOn] = useState("");
  const expires = expiry === "date" ? { on: expiryOn } : { months: Number(expiry) };
  const grant = useMutation({
    mutationFn: () =>
      kind === "grant"
        ? unwrap(api.POST("/v1/admin/orgs/{org_id}/lux", { params: { path: { org_id: orgId } }, body: { amount: amount ?? 0, expires, note: note || undefined } }))
        : unwrap(
            api.POST("/v1/admin/orgs/{org_id}/lux/refund", {
              params: { path: { org_id: orgId } },
              body: { operation_id: ref.trim(), amount: amount ?? null, note: note || undefined },
            }),
          ),
    onSuccess: (op) => {
      toast(t("admin.orgDetail.luxDone", { kind: op.kind, amount: lux(Math.abs(op.amount)) }));
      setNote("");
      setRef("");
      onDone();
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const ready = kind === "grant" ? !!amount && amount > 0 && (expiry !== "date" || !!expiryOn) : !!ref.trim();
  return (
    <div className="stack">
      <div className="form-grid">
        <Field label={t("admin.orgDetail.grantType")}>
          <Select
            value={kind}
            onChange={(k) => {
              setKind(k);
              setAmount(k === "refund" ? undefined : 1000);
            }}
            options={[
              { value: "grant", label: t("admin.orgDetail.grantTypeGrant") },
              { value: "refund", label: t("admin.orgDetail.grantTypeRefund") },
            ]}
          />
        </Field>
        <Field label={kind === "refund" ? t("admin.orgDetail.refundAmount") : t("admin.orgDetail.grantAmount")}>
          <LuxInput value={amount} onChange={setAmount} />
        </Field>
        {kind === "grant" && (
          <Field label={t("admin.orgDetail.expires")} hint={expiry === "date" ? t("admin.orgDetail.expiresOnHint") : t("admin.orgDetail.expiresInHint")}>
            <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
              <Select
                value={expiry}
                onChange={setExpiry}
                ariaLabel={t("admin.orgDetail.expires")}
                options={[
                  ...["1", "3", "6", "12"].map((m) => ({ value: m, label: t("admin.orgDetail.expiresInMonths", { count: Number(m) }) })),
                  { value: "date", label: t("admin.orgDetail.expiresOnDate") },
                ]}
              />
              {expiry === "date" && <input className="input" type="date" aria-label={t("admin.orgDetail.lastDay")} value={expiryOn} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setExpiryOn(e.target.value)} />}
            </div>
          </Field>
        )}
        {kind === "refund" && (
          <Field label={t("admin.orgDetail.chargeId")} hint={t("admin.orgDetail.chargeIdHint")} className="span-2">
            <TextInput value={ref} onChange={(e) => setRef(e.target.value)} placeholder={t("admin.orgDetail.chargeIdPlaceholder")} className="input mono" dir="ltr" />
          </Field>
        )}
        <Field label={t("admin.orgDetail.noteAudited")} className="span-2">
          <TextInput value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder={t("admin.orgDetail.grantNotePlaceholder")} />
        </Field>
      </div>
      <div>
        <Button variant="primary" icon={<LuxGlyph />} disabled={!ready} loading={grant.isPending} onClick={() => grant.mutate()}>
          {kind === "refund"
            ? amount
              ? t("admin.orgDetail.refundAmountButton", { amount: lux(amount) })
              : t("admin.orgDetail.refundChargeButton")
            : t("admin.orgDetail.grantButton", { amount: lux(amount ?? 0) })}
        </Button>
      </div>
    </div>
  );
}

function SchedulingForm({ orgId, scheduling, onDone }: { orgId: string; scheduling: S["OrganizationScheduling"]; onDone: () => void }) {
  const toast = useToast();
  const t = useT();
  const [s, setS] = useState(scheduling);
  useEffect(() => setS(scheduling), [scheduling]);
  const save = useMutation({
    mutationFn: () => unwrap(api.PUT("/v1/admin/orgs/{org_id}/scheduling", { params: { path: { org_id: orgId } }, body: { ...s, lane: s.lane || null } })),
    onSuccess: () => {
      toast(t("admin.orgDetail.schedulingSaved"));
      onDone();
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const dirty = JSON.stringify(s) !== JSON.stringify(scheduling);
  return (
    <div className="stack">
      <div className="form-grid">
        <Field label={t("admin.orgDetail.serviceTier")} hint={t("admin.orgDetail.serviceTierHint")}>
          <Select
            value={s.service_tier}
            onChange={(v) => setS({ ...s, service_tier: v })}
            options={(["free", "paid", "enterprise"] as const).map((tier) => ({ value: tier, label: t("admin.orgDetail.tierName", { tier }) }))}
          />
        </Field>
        <Field label={t("admin.orgDetail.priorityOverride")} hint={t("admin.orgDetail.priorityOverrideHint")}>
          <NumberInput value={s.priority_override} step={1} onChange={(v) => setS({ ...s, priority_override: v == null ? null : Math.round(v) })} />
        </Field>
        <Field label={t("admin.orgDetail.lane")} hint={t("admin.orgDetail.laneHint", { queue: "\u2066<class>@lane\u2069" })}>
          <TextInput value={s.lane ?? ""} placeholder={t("admin.orgDetail.lanePlaceholder")} dir="auto" onChange={(e) => setS({ ...s, lane: e.target.value.toLowerCase() || null })} className="input mono" />
        </Field>
        <Field label={t("admin.orgDetail.runningCap")} hint={t("admin.orgDetail.runningCapHint")}>
          <NumberInput value={s.max_running_override} min={0} step={1} onChange={(v) => setS({ ...s, max_running_override: v == null ? null : Math.round(v) })} />
        </Field>
        <div className="span-2">
          <Switch checked={s.lane_exclusive} onChange={(v) => setS({ ...s, lane_exclusive: v })} label={t("admin.orgDetail.laneExclusive")} />
        </div>
      </div>
      <div>
        <Button icon={<Save />} disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>
          {t("admin.orgDetail.saveScheduling")}
        </Button>
      </div>
    </div>
  );
}

/** How many utility prices the company may set (dashboard › Electricity prices); empty = the platform setting. */
function UtilityPriceLimit({ orgId }: { orgId: string }) {
  const toast = useToast();
  const t = useT();
  const { num } = useFmt();
  const qc = useQueryClient();
  const prices = useQuery({
    queryKey: ["electricity-prices", orgId],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/electricity-prices", { params: { path: { org_id: orgId } } })),
  });
  const [limit, setLimit] = useState<number | null>(null);
  useEffect(() => setLimit(prices.data && !prices.data.limit_is_default ? prices.data.limit : null), [prices.data]);
  const save = useMutation({
    mutationFn: () => unwrap(api.PUT("/v1/orgs/{org_id}/electricity-prices/limit", { params: { path: { org_id: orgId } }, body: { limit } })),
    onSuccess: (data) => {
      qc.setQueryData(["electricity-prices", orgId], data);
      toast(t("admin.orgDetail.limitSaved"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  if (!prices.data) return prices.error ? <Alert tone="danger">{errorMessage(prices.error)}</Alert> : <Spinner />;
  const d = prices.data;
  const current = d.limit_is_default ? null : d.limit;
  return (
    <div className="stack">
      <div className="form-grid">
        <Field
          label={t("admin.orgDetail.utilityLimit")}
          hint={d.limit_is_default ? t("admin.orgDetail.utilityLimitHintDefault", { limit: num(d.limit) }) : t("admin.orgDetail.utilityLimitHint")}
        >
          <NumberInput value={limit} min={0} max={10000} step={1} placeholder={t("admin.orgDetail.utilityLimitPlaceholder")} suffix={t("admin.orgDetail.utilityLimitUnit")} onChange={(v) => setLimit(v == null ? null : Math.round(v))} />
        </Field>
        <Field label={t("admin.orgDetail.inUse")}>
          <div className="input input-static">{t("admin.orgDetail.usedOfLimit", { used: num(d.used), limit: num(d.limit) })}</div>
        </Field>
      </div>
      <div>
        <Button icon={<Save />} disabled={limit === current} loading={save.isPending} onClick={() => save.mutate()}>
          {t("admin.orgDetail.saveLimit")}
        </Button>
      </div>
    </div>
  );
}

/** "Financing & suppliers" for this company: off by default, only platform admins switch it (audited; the company's
 * log shows Solar OS Support). Its partner choices stay stored while it is off. */
function PartnersAccess({ orgId, enabled, onDone }: { orgId: string; enabled: boolean; onDone: () => void }) {
  const toast = useToast();
  const t = useT();
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const save = useMutation({
    mutationFn: (on: boolean) =>
      unwrap(api.PUT("/v1/admin/orgs/{org_id}/partners-access", { params: { path: { org_id: orgId } }, body: { enabled: on, note: note.trim() || null } })),
    onSuccess: (o) => {
      toast(o.partners_enabled ? t("admin.orgDetail.partnersOnToast") : t("admin.orgDetail.partnersOffToast"));
      setNote("");
      qc.setQueryData(["org", orgId], o);
      qc.invalidateQueries({ queryKey: ["partners", orgId] });
      onDone();
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  return (
    <div className="stack">
      <Switch checked={enabled} disabled={save.isPending} onChange={(v) => save.mutate(v)} label={t("admin.orgDetail.partners")} />
      <p className="muted small" style={{ margin: 0 }}>
        {enabled
          ? t("admin.orgDetail.partnersOnHelp")
          : t("admin.orgDetail.partnersOffHelp")}
      </p>
      <Field label={t("admin.orgDetail.noteAudited")}>
        <TextInput value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder={t("admin.orgDetail.partnersNotePlaceholder")} />
      </Field>
    </div>
  );
}

function OwnerForm({ orgId, onDone }: { orgId: string; onDone: () => void }) {
  const toast = useToast();
  const t = useT();
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const move = useMutation({
    mutationFn: () => unwrap(api.PUT("/v1/admin/orgs/{org_id}/owner", { params: { path: { org_id: orgId } }, body: { email: email.trim(), note: note || undefined } })),
    onSuccess: () => {
      toast(t("admin.orgDetail.ownerMoved", { email: email.trim() }));
      setEmail("");
      setNote("");
      onDone();
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  return (
    <div className="stack">
      <Field label={t("admin.orgDetail.newOwnerEmail")} hint={t("admin.orgDetail.newOwnerEmailHint")}>
        <TextInput type="email" dir="ltr" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("admin.orgDetail.newOwnerEmailPlaceholder")} />
      </Field>
      <p className="muted small">{t("admin.orgDetail.oneOwner")}</p>
      <Field label={t("admin.orgDetail.noteAudited")}>
        <TextInput value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder={t("admin.orgDetail.ownerNotePlaceholder")} />
      </Field>
      <div>
        <Button icon={<Crown />} disabled={!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())} loading={move.isPending} onClick={() => move.mutate()}>
          {t("admin.orgDetail.moveOwner")}
        </Button>
      </div>
    </div>
  );
}

export function AdminOrgDetail() {
  const { orgId = "" } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const { lux, num, date } = useFmt();
  const [confirm, setConfirm] = useState<null | S["OrganizationStatus"]>(null);
  const [note, setNote] = useState("");
  const org = useQuery({
    queryKey: ["admin-org", orgId],
    queryFn: () => unwrap(api.GET("/v1/admin/orgs/{org_id}", { params: { path: { org_id: orgId } } })),
  });
  const wallet = useLuxWallet(orgId);
  const openCompany = useOpenCompany();
  const setStatus = useMutation({
    mutationFn: (status: S["OrganizationStatus"]) =>
      unwrap(api.PUT("/v1/admin/orgs/{org_id}/status", { params: { path: { org_id: orgId } }, body: { status, note: note || undefined } })),
    onSuccess: (o) => {
      toast(o.status === "suspended" ? t("admin.orgDetail.suspendedToast") : t("admin.orgDetail.reactivatedToast"));
      setConfirm(null);
      setNote("");
      refresh();
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-org", orgId] });
    qc.invalidateQueries({ queryKey: ["lux", orgId] });
    qc.invalidateQueries({ queryKey: ["lux-activity", orgId] });
    qc.invalidateQueries({ queryKey: ["admin-orgs"] });
  };

  if (org.isLoading) return <Spinner />;
  if (org.error || !org.data) return <Alert tone="danger">{errorMessage(org.error)}</Alert>;
  const { organization: o, balance, members, embed_keys, scheduling, seats } = org.data;
  const suspended = o.status === "suspended";

  return (
    <>
      <PageHead
        crumbs={
          <Link to="/admin/orgs">
            <DirIcon>
              <ArrowLeft size={12} style={{ verticalAlign: -1 }} />
            </DirIcon>{" "}
            {t("admin.orgDetail.back")}
          </Link>
        }
        title={
          <span className="row" style={{ gap: 12 }}>
            <span className="org-avatar" style={{ width: 38, height: 38, fontSize: 15 }}>{initials(o.name)}</span>
            <span dir="auto">{o.name}</span>
            <Badge tone={suspended ? "danger" : "ok"}>{t("admin.orgDetail.orgStatus", { status: o.status })}</Badge>
            <StatusBadge badge={o.badge} />
          </span>
        }
        description={<span className="mono small i18n-ltr">{o.id}</span>}
        actions={
          <>
            <Button variant="primary" icon={<DirIcon><LogIn /></DirIcon>} onClick={() => void openCompany(o.id)}>
              {t("admin.orgDetail.openDashboard")}
            </Button>
            <Link className="btn" to={`/admin/designs/clear?org=${o.id}`}>
              {t("admin.orgDetail.clearDesigns")}
            </Link>
            {suspended ? (
              <Button variant="primary" icon={<CheckCircle2 />} onClick={() => setConfirm("active")}>
                {t("admin.orgDetail.reactivate")}
              </Button>
            ) : (
              <Button variant="danger" icon={<Ban />} onClick={() => setConfirm("suspended")}>
                {t("admin.orgDetail.suspend")}
              </Button>
            )}
          </>
        }
      />
      {suspended && (
        <div style={{ marginBottom: 16 }}>
          <Alert tone="danger" title={t("admin.orgDetail.suspendedTitle")}>{t("admin.orgDetail.suspendedBody")}</Alert>
        </div>
      )}
      <div className="grid-4" style={{ marginBottom: 24 }}>
        <div className="card stat">
          <div className="label">Lux</div>
          <div className="value">{lux(balance)}</div>
          <div className="sub">
            {wallet.data
              ? t("admin.orgDetail.luxSub", { purchased: lux(wallet.data.purchased), monthly: lux(wallet.data.monthly.reserved_left + wallet.data.monthly.free_left) })
              : null}
          </div>
        </div>
        <div className="card stat">
          <div className="label">{t("admin.orgDetail.plan")}</div>
          <div className="value" style={{ fontSize: 20 }}>{t("admin.orgDetail.planName", { plan: o.plan || "free" })}</div>
          <div className="sub">{t("admin.orgDetail.planTier", { tier: scheduling.service_tier })}</div>
        </div>
        <div className="card stat">
          <div className="label">{t("admin.orgDetail.monthLux")}</div>
          <div className="value" style={{ fontSize: 20 }}>{wallet.data ? lux(wallet.data.monthly.reserved_granted + wallet.data.monthly.free_granted) : "—"}</div>
          <div className="sub">{wallet.data ? t("admin.orgDetail.monthLuxReserved", { reserved: lux(wallet.data.monthly.reserved_granted) }) : null}</div>
        </div>
        <div className="card stat">
          <div className="label">{t("admin.orgDetail.embedKeys")}</div>
          <div className="value">{num(embed_keys)}</div>
          <div className="sub">{t("admin.orgDetail.orgFacts", { country: o.country || "—", currency: o.currency || "—", since: date(o.created_at) })}</div>
        </div>
      </div>
      <div style={{ marginBottom: 24 }}>
        <OrgPlan orgId={o.id} />
      </div>
      <div style={{ marginBottom: 24 }}>
        <OrgBilling orgId={o.id} />
      </div>
      <div className="split">
        <div className="stack">
          <Card title="Lux" description={t("admin.orgDetail.luxCardDescription")}>
            <GrantForm orgId={orgId} onDone={refresh} />
          </Card>
          <Activity orgId={orgId} showIds compact />
        </div>
        <div className="stack">
          <Card title={t("admin.orgDetail.members")} description={t("admin.orgDetail.seats", { active: seats.active, invited: seats.invited, suspended: seats.suspended })} pad={false}>
            <div className="table-wrap">
              <table className="table">
                <tbody>
                  {members.map((m) => (
                    <tr key={m.user_id} style={m.status !== "active" ? { opacity: 0.65 } : undefined}>
                      <td>
                        <div className="cell-main" dir="auto">{m.display_name || m.email}</div>
                        <div className="cell-sub">
                          {m.display_name
                            ? t("admin.orgDetail.memberSubWithEmail", { email: m.email, source: m.source, joined: date(m.created_at) })
                            : t("admin.orgDetail.memberSub", { source: m.source, joined: date(m.created_at) })}
                        </div>
                      </td>
                      <td className="right nowrap">
                        {m.status !== "active" && <Badge tone="danger">{t("admin.orgDetail.memberStatus", { status: m.status })}</Badge>}{" "}
                        <Badge plain tone={ROLE_TONE[m.role]}>{t("admin.orgDetail.memberRole", { role: m.role })}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          <Card title={<span className="row" style={{ gap: 8 }}><Crown size={16} /> {t("admin.orgDetail.ownership")}</span>} description={t("admin.orgDetail.ownershipDescription")}>
            <OwnerForm orgId={orgId} onDone={refresh} />
          </Card>
          <Card title={<span className="row" style={{ gap: 8 }}><Gauge size={16} /> {t("admin.orgDetail.scheduling")}</span>} description={t("admin.orgDetail.schedulingDescription")}>
            <SchedulingForm orgId={orgId} scheduling={scheduling} onDone={refresh} />
          </Card>
          <Card
            title={<span className="row" style={{ gap: 8 }}><Handshake size={16} /> {t("admin.orgDetail.partners")}</span>}
            description={t("admin.orgDetail.partnersDescription")}
          >
            <PartnersAccess orgId={orgId} enabled={o.partners_enabled} onDone={refresh} />
          </Card>
          <Card title={<span className="row" style={{ gap: 8 }}><Zap size={16} /> {t("admin.orgDetail.electricityPrices")}</span>} description={t("admin.orgDetail.electricityPricesDescription")}>
            <UtilityPriceLimit orgId={orgId} />
          </Card>
        </div>
      </div>
      <div style={{ marginTop: 24 }}>
        <Card
          pad={false}
          title={<span className="row" style={{ gap: 8 }}><Workflow size={16} /> {t("admin.orgDetail.pipeline")}</span>}
          description={t("admin.orgDetail.pipelineDescription")}
        >
          <OrgPipeline orgId={orgId} />
        </Card>
      </div>
      <div style={{ marginTop: 24 }}>
        <Card
          pad={false}
          title={<span className="row" style={{ gap: 8 }}><Blocks size={16} /> {t("admin.orgDetail.integrations")}</span>}
          description={t("admin.orgDetail.integrationsDescription")}
        >
          <OrgIntegrations orgId={orgId} />
        </Card>
      </div>
      {confirm && (
        <Modal
          title={confirm === "suspended" ? t("admin.orgDetail.suspendTitle", { name: o.name }) : t("admin.orgDetail.reactivateTitle", { name: o.name })}
          onClose={() => setConfirm(null)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirm(null)}>
                {t("admin.common.cancel")}
              </Button>
              <Button variant={confirm === "suspended" ? "danger" : "primary"} loading={setStatus.isPending} onClick={() => setStatus.mutate(confirm)}>
                {confirm === "suspended" ? t("admin.orgDetail.suspend") : t("admin.orgDetail.reactivate")}
              </Button>
            </>
          }
        >
          <div className="stack">
            <p className="muted">
              {confirm === "suspended"
                ? t("admin.orgDetail.suspendBody")
                : t("admin.orgDetail.reactivateBody")}
            </p>
            <Field label={t("admin.orgDetail.noteAudited")}>
              <TextInput value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("admin.orgDetail.reasonPlaceholder")} />
            </Field>
          </div>
        </Modal>
      )}
    </>
  );
}
